import { DateRefinement } from '~/apps/product-plus/components/BettingInsights/components/Filters';
import { MatchupCard } from '~/apps/product-plus/components/BettingInsights/components/MatchupCard';
import { Autocomplete } from '~/apps/product-plus/components/BettingInsights/components/Search';
import {
  type BettingInsightsConfigFactoryOptions,
  type MatchupCentreIndex,
} from '~/apps/product-plus/configuration/types';
import {
  type BettingInsightsUIConfig,
  createBettingInsightsUIConfig,
} from '~/apps/product-plus/configuration/ui/BettingInsightsUI';
import { CATEGORY_ORDERING_CONFIGS } from '~/apps/product-plus/repub-config/category-ordering-config';
import { FILTERS_CONFIGS } from '~/apps/product-plus/search-domain/config/filters-config';
import {
  EVENT_DISPLAY_NAME,
  getEventDisplayNames,
} from '~/apps/product-plus/search-domain/constants/eventDisplayNames';
import { matchupCentreIndex } from '~/apps/product-plus/search-domain/indexes';
import { REPUB_CONFIG } from '~/repub-config';
import { type RequestContext } from '~/types/RequestContext';

export function createMatchupCentreUIConfig(
  requestContext: RequestContext,
  _options: BettingInsightsConfigFactoryOptions = {},
  tags: string[],
) {
  const filterTranslations = FILTERS_CONFIGS[requestContext.repubConfig.name as keyof typeof FILTERS_CONFIGS];

  const uiConfig: BettingInsightsUIConfig<MatchupCentreIndex, any> = {
    card: {
      styles: {},
      wrapper: (_context, { children }) => children,
      table: {
        body: {
          render(_context, { bookmakers, content, resourcesUrl, table }) {
            return (
              <MatchupCard
                bookmakers={bookmakers}
                content={content}
                isLocked={table.isWalled}
                resourcesUrl={resourcesUrl || ''}
              />
            );
          },
        },
        getContent(_context, { result }) {
          return [result];
        },
        headings: {
          content: [],
        },
        cells: [],
      },
      title: {
        format: 'Matchup Centre',
      },
    },
    initialUiState(context) {
      const plusConfig = REPUB_CONFIG[context.repubConfig.name]?.plus;

      return {
        hitsPerPage: 10,
        range: {
          'sportsData.hitRates.last10': '60:100',
        },
        refinementList: {
          eventDisplayName: [plusConfig?.matchupCentre.defaultEventDisplayName ?? 'EPL'],
        },
      };
    },
    refinements: {
      clearAll: {
        excludedAttributes: [],
      },
      components: [
        {
          attribute: 'subeventStartDate',
          getLabel(_context) {
            return filterTranslations.date;
          },
          render(context) {
            return (
              <DateRefinement
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label={filterTranslations.date}
              />
            );
          },
        },
      ],
    },
    results: {
      categories(context) {
        const repubName = context.repubConfig.name;
        const categoryOrdering =
          CATEGORY_ORDERING_CONFIGS[repubName as keyof typeof CATEGORY_ORDERING_CONFIGS] ??
          CATEGORY_ORDERING_CONFIGS.OC;

        return {
          attribute: EVENT_DISPLAY_NAME,
          labels: getEventDisplayNames(repubName),
          order: categoryOrdering,
        };
      },
      labelledBy: '#bet-name',
      propsName: 'bets',
    },
    search: {
      render(context, { searchAttributes }) {
        return (
          <Autocomplete
            disabled={context.isWalled}
            indexName={matchupCentreIndex}
            placeholder={filterTranslations.matchCenterSearchPlaceholder}
            repub={context.repubConfig.name}
            searchAttributes={searchAttributes}
          />
        );
      },
    },
    sorting: {
      canSort: () => false,
    },
  };

  return { ui: createBettingInsightsUIConfig(requestContext, uiConfig, tags) };
}
